"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import {
  uploadCreativeTemplateAsset,
  generateCreativeTemplate,
  getCreativeTemplateSignedUrl,
} from "@/app/_api/creativeTemplates";
import type { ReferenceValue } from "@/app/_api/flowTemplates";
import type { FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { HttpError } from "@utilities/api";
import { getMediaDuration } from "@utilities/videoUtils";
import { useAutoDismissError } from "@/app/_ui/hooks/useAutoDismissError";
import { getUserData } from "@/app/_providers/user";
import { appUrls, buildSignUpRedirectUrl } from "@/constants";
import { USER_INPUT_PLACEHOLDER } from "../../constants";
import type { ContentWorkflowStep, GenerationModel, WorkflowInputConfig } from "../types";
import { runFlowAndAwaitResult } from "./runFlowAndAwaitResult";

/**
 * Resolves the CTA destination for the current visitor. Logged-in users go
 * straight to the app destination; anonymous visitors are routed through
 * sign-up with the destination preserved as a `redirect` deep link.
 */
function toCtaUrl(destinationUrl: string): string {
  return getUserData() ? destinationUrl : buildSignUpRedirectUrl(destinationUrl);
}

function buildFlowTemplateRedirectUrl({
  flowTemplate,
  assetIdsByPort,
  textsByPort,
}: {
  flowTemplate: FlowTemplateSchema;
  assetIdsByPort: Record<string, string>;
  textsByPort?: Record<string, string>;
}): string {
  const url = new URL(`/app/templates/${encodeURIComponent(flowTemplate.id)}`, appUrls.root);
  for (const [portId, assetId] of Object.entries(assetIdsByPort)) {
    url.searchParams.set(`input.${portId}`, assetId);
  }
  if (textsByPort) {
    for (const [portId, text] of Object.entries(textsByPort)) {
      if (assetIdsByPort[portId]) continue;
      const trimmed = text.trim();
      if (trimmed) url.searchParams.set(`input.${portId}`, trimmed);
    }
  }
  return url.toString();
}

interface GenerateOutcome {
  resultUrl: string;
  resultAssetId: string;
  modelId: string | null;
  refinedPrompt: string | null;
}

function buildRedirectUrl(redirectUrlTemplate: string, assetId?: string, userInput?: string): string {
  const url = new URL(redirectUrlTemplate);
  if (assetId) url.searchParams.set("asset_id", assetId);
  const prompt = url.searchParams.get("prompt");
  if (prompt && userInput) {
    url.searchParams.set("prompt", prompt.replaceAll(USER_INPUT_PLACEHOLDER, userInput));
  }
  return url.toString();
}

function buildClaimUrl({
  assetId,
  modelId,
  refinedPrompt,
}: {
  assetId: string;
  modelId?: string | null;
  refinedPrompt?: string | null;
}): string {
  const url = new URL(`${appUrls.imageVideo}/history`);
  url.searchParams.set("claim_asset_id", assetId);
  if (modelId) url.searchParams.set("model", modelId);
  if (refinedPrompt) url.searchParams.set("prompt", refinedPrompt);
  return url.toString();
}

function buildFlowTemplateClaimUrl({
  flowTemplate,
  sessionId,
}: {
  flowTemplate: FlowTemplateSchema;
  sessionId: string;
}): string {
  const url = new URL(`/app/templates/${encodeURIComponent(flowTemplate.id)}`, appUrls.root);
  url.searchParams.set("claim_session_id", sessionId);
  return url.toString();
}

export interface UseContentWorkflowReturn {
  step: ContentWorkflowStep;
  userInput: string | null;
  resultUrl: string | null;
  redirectCtaUrl: string | null;
  inputPreviewUrl: string | null;
  selectedFile: File | null;
  error: string | null;
  handleFileSelected: (file: File) => void;
  handleTextSubmit: (text: string) => void;
  handleSubmit: (userInput?: string) => void;
  handleRemoveFile: () => void;
  handleFlowSubmit: (form: {
    files: Record<string, File>;
    texts: Record<string, string>;
    prefilledAssetsByPort: Record<string, { assetId: string; previewUrl: string }>;
  }) => void;
  validateFile: (file: File) => Promise<string | null>;
  restart: () => void;
}

export function useCreativeTemplate({
  templateSlug,
  templateCategory,
  variantSlug,
  generationModel,
  config,
  redirectUrlTemplate,
  promptRequiresInput = false,
  flowTemplate,
}: {
  templateSlug: string;
  templateCategory: string;
  variantSlug: string;
  generationModel: GenerationModel;
  config: WorkflowInputConfig;
  redirectUrlTemplate: string;
  promptRequiresInput?: boolean;
  flowTemplate?: FlowTemplateSchema | null;
}): UseContentWorkflowReturn {
  const [step, setStep] = useState<ContentWorkflowStep>("idle");
  const [userInput, setUserInput] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [redirectCtaUrl, setRedirectCtaUrl] = useState<string | null>(null);
  const [inputPreviewUrl, setInputPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { error, setError } = useAutoDismissError();

  const t = useTranslations("ClientKeys.creative_templates");
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputPreviewUrlRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (inputPreviewUrlRef.current) {
      URL.revokeObjectURL(inputPreviewUrlRef.current);
      inputPreviewUrlRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const executeWorkflow = useCallback(
    async ({ file, prompt }: { file?: File; prompt?: string }) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      let assetId: string | undefined;
      setUserInput(prompt ?? null);

      captureEvent(EventName.mw_creative_template_omnibox_generation_started, {
        template_category: templateCategory,
        variant_slug: variantSlug,
        input_type: file ? "file" : "text",
        generation_model: generationModel,
      });

      try {
        // Upload step (for file inputs)
        if (file) {
          setStep("uploading");
          const uploadResult = await uploadCreativeTemplateAsset(file, abortController.signal);
          assetId = uploadResult.asset_id;

          if (abortController.signal.aborted) return;
        }

        // Video templates require an account — skip the generate call entirely
        // to avoid counting against the global daily rate limit for image generation.
        if (config.resultType === "video") {
          captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
            template_category: templateCategory,
            variant_slug: variantSlug,
            error_type: "rate_limited",
          });
          setRedirectCtaUrl(toCtaUrl(buildRedirectUrl(redirectUrlTemplate, assetId, prompt)));
          setStep("rate_limited");
          return;
        }

        setStep("generating");

        const generateResult = await generateCreativeTemplate({
          templateSlug,
          assetId,
          userInput: prompt,
          signal: abortController.signal,
        });
        const resultAssetId = generateResult.asset_id;
        if (!resultAssetId) {
          throw new Error("No asset ID returned from generation");
        }
        const { url } = await getCreativeTemplateSignedUrl(resultAssetId, abortController.signal);
        const outcome: GenerateOutcome = {
          resultUrl: url,
          resultAssetId,
          modelId: generateResult.model_id,
          refinedPrompt: generateResult.refined_prompt,
        };

        if (abortController.signal.aborted) return;

        captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, {
          template_category: templateCategory,
          variant_slug: variantSlug,
          input_type: file ? "file" : "text",
          generation_model: generationModel,
        });

        setRedirectCtaUrl(
          toCtaUrl(
            buildClaimUrl({
              assetId: outcome.resultAssetId,
              modelId: outcome.modelId,
              refinedPrompt: outcome.refinedPrompt ?? prompt,
            }),
          ),
        );
        setResultUrl(outcome.resultUrl);
        setStep("result");
      } catch (err) {
        if (abortController.signal.aborted) return;

        const errorType = err instanceof HttpError ? err.errorType : null;

        switch (errorType) {
          case "image_too_large":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t("error_image_too_large"));
            setStep("file_selected");
            break;

          case "generation_refused":
          case "cms_unreachable":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t(errorType === "generation_refused" ? "error_generation_refused" : "error_cms_unreachable"));
            setStep(file ? "file_selected" : "idle");
            break;

          case "quota_exceeded":
          case "rate_limited": {
            captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });

            const refinedPrompt =
              err instanceof HttpError ? (err.data?.refined_prompt as string | undefined) : undefined;
            const redirectUrl = new URL(buildRedirectUrl(redirectUrlTemplate, assetId, prompt));
            if (refinedPrompt) redirectUrl.searchParams.set("prompt", refinedPrompt);
            setRedirectCtaUrl(toCtaUrl(redirectUrl.toString()));

            setStep(errorType === "rate_limited" ? "rate_limited" : "quota_exceeded");
            break;
          }

          default:
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_message: err instanceof Error ? err.message : "Unknown error",
              generation_model: generationModel,
            });
            setError(t("error_generic"));
            setStep("idle");
        }
      }
    },
    [templateSlug, templateCategory, variantSlug, generationModel, redirectUrlTemplate, config.resultType, setError, t],
  );

  // Flow templates inherit `config` from the doc's legacy WORKFLOW_INPUT_CONFIG,
  // which may declare `maxFileSizeMb: 0` / `maxVideoDurationSec: 0` for
  // text-only workflows. A flow template with image/video ports linked to such
  // a doc would otherwise bypass size + duration validation entirely. Apply
  // safe defaults so flow-template uploads are always bounded.
  const maxFileSizeMb = config.maxFileSizeMb > 0 ? config.maxFileSizeMb : flowTemplate ? 20 : 0;
  const maxVideoDurationSec = config.maxVideoDurationSec > 0 ? config.maxVideoDurationSec : flowTemplate ? 30 : 0;

  const validateFile = useCallback(
    async (file: File): Promise<string | null> => {
      if (maxFileSizeMb > 0 && file.size > maxFileSizeMb * 1024 * 1024) {
        return t("error_file_too_large", { maxSize: maxFileSizeMb });
      }
      if (maxVideoDurationSec > 0 && file.type.startsWith("video/")) {
        try {
          const duration = await getMediaDuration(file);
          if (duration > maxVideoDurationSec) {
            return t("error_video_too_long", { maxDuration: maxVideoDurationSec });
          }
        } catch {
          return t("error_video_invalid");
        }
      }
      return null;
    },
    [maxFileSizeMb, maxVideoDurationSec, t],
  );

  const handleFileSelected = useCallback(
    async (file: File) => {
      const validationError = await validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setError(null);

      // Create preview URL
      if (inputPreviewUrlRef.current) {
        URL.revokeObjectURL(inputPreviewUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      inputPreviewUrlRef.current = previewUrl;
      setInputPreviewUrl(previewUrl);

      setSelectedFile(file);

      if (promptRequiresInput) {
        setStep("file_selected");
      } else {
        executeWorkflow({ file });
      }
    },
    [validateFile, promptRequiresInput, executeWorkflow, setError],
  );

  const handleSubmit = useCallback(
    (userInput?: string) => {
      if (selectedFile) {
        executeWorkflow({ file: selectedFile, prompt: userInput });
      }
    },
    [selectedFile, executeWorkflow],
  );

  const handleRemoveFile = useCallback(() => {
    if (inputPreviewUrlRef.current) {
      URL.revokeObjectURL(inputPreviewUrlRef.current);
      inputPreviewUrlRef.current = null;
    }
    setSelectedFile(null);
    setInputPreviewUrl(null);
    setStep("idle");
    setError(null);
  }, [setError]);

  const handleTextSubmit = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      setError(null);
      executeWorkflow({ prompt: text.trim() });
    },
    [executeWorkflow, setError],
  );

  const handleFlowSubmit = useCallback(
    async (form: {
      files: Record<string, File>;
      texts: Record<string, string>;
      prefilledAssetsByPort: Record<string, { assetId: string; previewUrl: string }>;
    }) => {
      if (!flowTemplate) return;

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setError(null);

      const fileEntries = Object.entries(form.files);
      const firstFile = fileEntries[0]?.[1];
      const firstText = Object.values(form.texts).find(v => v.trim().length > 0);
      const firstPrefilledAsset = Object.values(form.prefilledAssetsByPort)[0];

      // Hero backdrop during "generating": only meaningful when the flow has a
      // single input port — there's then a canonical "this is what I sent" image
      // to keep visible under the running-workflow pill. Multi-input flows have
      // no obvious pick, so leave preview null and let DynamicHeroMedia render
      // the striped loader instead of an arbitrary first-port image.
      const isSingleInputPort = flowTemplate.input_ports.length === 1;
      if (!isSingleInputPort) {
        if (inputPreviewUrlRef.current) {
          URL.revokeObjectURL(inputPreviewUrlRef.current);
          inputPreviewUrlRef.current = null;
        }
        setInputPreviewUrl(null);
        setSelectedFile(null);
      } else if (firstFile) {
        if (inputPreviewUrlRef.current) URL.revokeObjectURL(inputPreviewUrlRef.current);
        const previewUrl = URL.createObjectURL(firstFile);
        inputPreviewUrlRef.current = previewUrl;
        setInputPreviewUrl(previewUrl);
        setSelectedFile(firstFile);
      } else if (firstPrefilledAsset) {
        if (inputPreviewUrlRef.current) {
          URL.revokeObjectURL(inputPreviewUrlRef.current);
          inputPreviewUrlRef.current = null;
        }
        setInputPreviewUrl(firstPrefilledAsset.previewUrl);
      }
      setUserInput(firstText ?? null);

      captureEvent(EventName.mw_creative_template_omnibox_generation_started, {
        template_category: templateCategory,
        variant_slug: variantSlug,
        input_type: firstFile || firstPrefilledAsset ? "file" : "text",
        generation_model: generationModel,
      });

      // Seed with prefilled examples; user-uploaded files overwrite below so a
      // real upload always wins on a colliding port.
      let assetIdsByPort: Record<string, string> = Object.fromEntries(
        Object.entries(form.prefilledAssetsByPort).map(([portId, asset]) => [portId, asset.assetId]),
      );
      try {
        if (fileEntries.length > 0) {
          setStep("uploading");
          // Per-port uploads run in parallel; on a single failure we let siblings
          // complete (low blast radius for anonymous flows) so the shared abort
          // signal stays unaborted unless the USER cancelled — that distinction
          // is what the outer catch's signal.aborted check depends on.
          const uploaded = await Promise.all(
            fileEntries.map(async ([portId, file]) => {
              const { asset_id } = await uploadCreativeTemplateAsset(file, abortController.signal);
              return [portId, asset_id] as const;
            }),
          );
          if (abortController.signal.aborted) return;
          assetIdsByPort = { ...assetIdsByPort, ...Object.fromEntries(uploaded) };
        }

        if (config.resultType === "video") {
          captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
            template_category: templateCategory,
            variant_slug: variantSlug,
            error_type: "rate_limited",
          });
          setRedirectCtaUrl(
            toCtaUrl(buildFlowTemplateRedirectUrl({ flowTemplate, assetIdsByPort, textsByPort: form.texts })),
          );
          setStep("rate_limited");
          return;
        }

        setStep("generating");

        const inputs: Record<string, ReferenceValue> = {};
        for (const [portId, assetId] of Object.entries(assetIdsByPort)) {
          inputs[portId] = { content_asset_id: assetId };
        }
        for (const [portId, text] of Object.entries(form.texts)) {
          if (assetIdsByPort[portId]) continue;
          const trimmed = text.trim();
          if (trimmed) inputs[portId] = { inline_text: trimmed };
        }

        const flowResult = await runFlowAndAwaitResult({
          flowTemplate,
          inputs,
          resultType: config.resultType,
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) return;

        captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, {
          template_category: templateCategory,
          variant_slug: variantSlug,
          input_type: firstFile || firstPrefilledAsset ? "file" : "text",
          generation_model: generationModel,
        });

        setRedirectCtaUrl(toCtaUrl(buildFlowTemplateClaimUrl({ flowTemplate, sessionId: flowResult.sessionId })));
        setResultUrl(flowResult.resultUrl);
        setStep("result");
      } catch (err) {
        if (abortController.signal.aborted) return;

        const errorType = err instanceof HttpError ? err.errorType : null;

        switch (errorType) {
          case "image_too_large":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t("error_image_too_large"));
            setStep("idle");
            break;

          case "generation_refused":
          case "cms_unreachable":
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setError(t(errorType === "generation_refused" ? "error_generation_refused" : "error_cms_unreachable"));
            setStep("idle");
            break;

          case "quota_exceeded":
          case "rate_limited": {
            captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_type: errorType,
              generation_model: generationModel,
            });
            setRedirectCtaUrl(
              toCtaUrl(buildFlowTemplateRedirectUrl({ flowTemplate, assetIdsByPort, textsByPort: form.texts })),
            );
            setStep(errorType === "rate_limited" ? "rate_limited" : "quota_exceeded");
            break;
          }

          default:
            captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
              template_category: templateCategory,
              variant_slug: variantSlug,
              error_message: err instanceof Error ? err.message : "Unknown error",
              generation_model: generationModel,
            });
            setError(t("error_generic"));
            setStep("idle");
        }
      }
    },
    [flowTemplate, templateCategory, variantSlug, generationModel, config.resultType, setError, t],
  );

  const restart = useCallback(() => {
    cleanup();
    setStep("idle");
    setResultUrl(null);
    setRedirectCtaUrl(null);
    setInputPreviewUrl(null);
    setSelectedFile(null);
    setError(null);
  }, [cleanup, setError]);

  return {
    step,
    userInput,
    resultUrl,
    redirectCtaUrl,
    inputPreviewUrl,
    selectedFile,
    error,
    handleFileSelected,
    handleTextSubmit,
    handleSubmit,
    handleRemoveFile,
    handleFlowSubmit,
    validateFile,
    restart,
  };
}
